import router from 'next/router';
import dayjs from 'dayjs';
import fetchNext from '@features/api/fetchNext';
import { IAdditionalArgument, IAdditionalArguments, IAdditionalArgumentsRecord, ICartAvailableDelivery, ICartData, TAdditionalDeliveryAddressParameter, DeliveryType } from '@features/cart/typescript';
import { IAddressesIDs, IServicePrice, IOnlinePaymentProvider, ISessionCustomerData, ISelectedShop, ICartDeliveriesResponse, IDeliveryDestination, IDeliveryItem } from '@features/checkout/typescript';
import getPublicEnv from '@utils/getPublicEnv';
import { countBusinessDays } from '@utils/addBusinessDays';
class NextCheckoutApi {
  public endpoints = {
    createCart: () => `cart/create`,
    getCart: (cartId: string) => `cart/${cartId}`,
    getAvailableDeliveries: (cartId: string, transportGroupId: string, additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord) => {
      const baseURI = `cart/${cartId}/transport/${transportGroupId}/available-deliveries`;
      if (!additionalArguments) return baseURI;

      // Obsługa różnych typów argumentów
      if ('key' in additionalArguments && 'value' in additionalArguments) {
        // Pojedynczy IAdditionalArgument
        return `${baseURI}?additionalArgumentKey=${additionalArguments.key}&additionalArgumentValue=${additionalArguments.value}`;
      } else if ('arguments' in additionalArguments && Array.isArray(additionalArguments.arguments)) {
        // IAdditionalArguments (tablica)
        const queryParams = additionalArguments.arguments.map(arg => `${encodeURIComponent(arg.key)}=${encodeURIComponent(arg.value)}`).join('&');
        return `${baseURI}?${queryParams}`;
      } else if (typeof additionalArguments === 'object') {
        // IAdditionalArgumentsRecord (Record<string, string>)
        const queryParams = Object.entries(additionalArguments).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
        return `${baseURI}?${queryParams}`;
      }
      return baseURI;
    },
    addPositions: (cartId: string) => `cart/${cartId}/add`,
    deletePosition: (cartId: string, positionId: string) => `cart/${cartId}/positions/${positionId}`,
    changeQuantity: (cartId: string, positionId: string) => `cart/${cartId}/positions/${positionId}/quantity`,
    changeDelivery: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/changeDelivery`,
    assignCustomerToCart: (cartId: string) => `cart/${cartId}/customer`,
    getAvailableDigitalTaxReceiptUsage: () => 'cart/available-digital-tax-receipt-usage',
    getTransactionMethods: () => `cart/transaction-methods`,
    createOrder: () => `cart/create-order`,
    getOrderPaymentDetails: () => `order/payment/details`,
    getOrderDetails: () => `order/details`,
    clearOrder: () => `order/clear`,
    confirmPaypalPayment: (tokenId: string) => `paypal/${tokenId}/return`,
    cancelPaypalPayment: (tokenId: string) => `paypal/${tokenId}/cancel`,
    confirmOpenpayPayment: (tokenId: string) => `openpay/${tokenId}/return`,
    confirmBNPPayment: (orderId: string, issueNumber: string) => `order/${orderId}/bnp/${issueNumber}/confirm`,
    selectedPayment: () => `checkout/selectedPayment`,
    selectedShop: () => `checkout/selectedShop`,
    selectedDigitalTaxReceiptUsageDataId: () => `checkout/selectedDigitalTaxReceiptUsageDataId`,
    checkoutAddressCollection: () => `checkout/checkoutAddressCollection`,
    sessionCustomerData: () => `checkout/customerData`,
    checkDiscount: (cartId: string) => `cart/${cartId}/discount-code`,
    selectedTaxRegimeDataId: () => `checkout/selectedTaxRegimeDataId`,
    getPointsOfDelivery: (cartId: string, location: GeolocationCoordinates | null, transportGroupId: string) => `cart/${cartId}/pointsOfDelivery?transportGroupId=${transportGroupId}&latitude=${location?.latitude || ''}&longitude=${location?.longitude || ''}`,
    getPointsOfSale: () => `cart/pointsOfSale`,
    checkBringingService: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/bring`,
    getBringingServicePrice: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/precalculate-service-v2/bring`,
    checkAssemblyService: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/assembly`,
    getAssemblyServicePrice: (cartId: string, transportGroupId: string) => `cart/${cartId}/transport/${transportGroupId}/precalculate-service-v2/assembly`,
    getBNPHash: () => `order/hash`,
    sessionNote: () => `checkout/note`,
    service: (cartId: string, transportGroupId: string, serviceId: string) => `cart/${cartId}/transport/${transportGroupId}/service/${serviceId}`,
    cartAgreement: (cartId: string) => `cart/${cartId}/agreement`,
    getCartDeliveries: (cartId: string, destination?: IDeliveryDestination) => {
      const baseUrl = `cart/${cartId}/deliveries`;
      if (!destination) {
        return baseUrl;
      }
      const params = new URLSearchParams();
      if (destination.postalCode) {
        params.append('destination[postalCode]', destination.postalCode);
      }
      if (destination.countryCode) {
        params.append('destination[countryCode]', destination.countryCode);
      }
      return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    }
  };
  private static getLocale() {
    const isServerSide = typeof window === 'undefined';
    if (isServerSide) return process.env.DEFAULT_LOCALE;
    const {
      locale
    } = router;
    return locale || getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE');
  }
  public async createCart() {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.createCart(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getCart(cartId) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getCart(cartId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getAvailableDeliveries(cartId: string, transportGroupId: string, additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord) {
    try {
      // Konwertuj argumenty na IDeliveryDestination
      let destination = this.convertAdditionalArgumentsToDestination(additionalArguments);
      if (!destination) {
        const {
          locale
        } = router;
        destination = {
          countryCode: process.env?.DEFAUL_DELIVERY_LOCALE || locale?.toUpperCase() || 'PL'
        };
      }

      // Pobierz dane z nowego endpointu
      const deliveriesResponse = await this.getCartDeliveries(cartId, destination);

      // Znajdź dane - sprawdź czy to cartId czy transportGroupId
      let cartDeliveries = deliveriesResponse[cartId];
      if (!cartDeliveries) {
        cartDeliveries = deliveriesResponse[transportGroupId];
      }
      if (!cartDeliveries || !Array.isArray(cartDeliveries)) {
        return {
          success: true,
          data: []
        };
      }

      // Konwertuj IDeliveryItem[] na ICartAvailableDelivery[]
      const convertedDeliveries: ICartAvailableDelivery[] = cartDeliveries.map(deliveryItem => this.convertDeliveryItemToCartAvailableDelivery(deliveryItem, destination?.postalCode || ''));
      return {
        success: true,
        data: convertedDeliveries
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Nieznany błąd podczas pobierania dostaw'
      };
    }
  }
  public async addPositions(cartId: string, productArray: ICartData[]) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.addPositions(cartId), {
      method,
      body: JSON.stringify({
        products: productArray
      })
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async deletePosition(cartId: string, positionId: string) {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.deletePosition(cartId, positionId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async changePositionQuantity(cartId: string, positionId: string, quantity: number) {
    const method = 'PATCH';
    const {
      json
    } = await fetchNext(this.endpoints.changeQuantity(cartId, positionId), {
      method,
      body: JSON.stringify({
        quantity
      })
    });
    return json;
  }
  public async changeDelivery(cartId: string, delivery: ICartAvailableDelivery, transportGroupId: string, additionalDeliveryAddressParameters?: TAdditionalDeliveryAddressParameter) {
    const method = 'PATCH';
    const requestData: {
      delivery: ICartAvailableDelivery;
      cartId: string;
      additionalDeliveryAddressParameters?: Record<string, string>;
    } = {
      delivery,
      cartId
    };
    if (additionalDeliveryAddressParameters) {
      requestData.additionalDeliveryAddressParameters = Object.fromEntries(Object.entries(additionalDeliveryAddressParameters));
    }
    const {
      json
    } = await fetchNext(this.endpoints.changeDelivery(cartId, transportGroupId), {
      method,
      body: JSON.stringify({
        ...requestData
      })
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async assignCustomerToCart(cartId: string, customerId: string) {
    const method = 'PATCH';
    const {
      json
    } = await fetchNext(this.endpoints.assignCustomerToCart(cartId), {
      method,
      body: JSON.stringify({
        customerId
      })
    });
    return json;
  }
  public async getAvailableDigitalTaxReceiptUsage() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getAvailableDigitalTaxReceiptUsage(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async clearOrder() {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.clearOrder(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async createOrder(cartId: string, customerId: string) {
    const method = 'POST';
    const body = JSON.stringify({
      cartId,
      customerId
    });
    const {
      json
    } = await fetchNext(this.endpoints.createOrder(), {
      method,
      body
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getTransactionMethods() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getTransactionMethods(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getOrderPaymentDetails() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getOrderPaymentDetails(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getOrderDetails() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getOrderDetails(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async confirmPaypalPayment(tokenId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.confirmPaypalPayment(tokenId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async cancelPaypalPayment(tokenId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.cancelPaypalPayment(tokenId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async confirmOpenpayPayment(tokenId: string): Promise<JSON> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.confirmOpenpayPayment(tokenId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async confirmBNPPayment(orderId: string, issueNumber: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.confirmBNPPayment(orderId, issueNumber), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setSelectedPayment(selectedPayment: IOnlinePaymentProvider) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedPayment(), {
      method,
      body: JSON.stringify(selectedPayment)
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSelectedPayment() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedPayment(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setSelectedShop(selectedShop: ISelectedShop | null) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedShop(), {
      method,
      body: JSON.stringify(selectedShop)
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSelectedShop() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedShop(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setSelectedDigitalTaxReceiptUsageDataId(selectedDigitalTaxReceiptUsageDataId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedDigitalTaxReceiptUsageDataId(), {
      method,
      body: JSON.stringify({
        selectedDigitalTaxReceiptUsageDataId
      })
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSelectedDigitalTaxReceiptUsageDataId() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedDigitalTaxReceiptUsageDataId(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setCheckoutAddressCollection(checkoutAddressCollection: IAddressesIDs) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkoutAddressCollection(), {
      method,
      body: JSON.stringify(checkoutAddressCollection)
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getCheckoutAddressCollection() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.checkoutAddressCollection(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setSessionCustomerData(customerData: ISessionCustomerData) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.sessionCustomerData(), {
      method,
      body: JSON.stringify(customerData)
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSessionCustomerData() {
    if (process.env.IS_CUSTOMER !== 'true') return {};
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.sessionCustomerData(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async checkDiscountCode(cartId: string, code: string): Promise<JSON> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkDiscount(cartId), {
      method,
      body: JSON.stringify({
        code
      })
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async removeDiscountCode(cartId: string): Promise<JSON> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkDiscount(cartId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async addBringingService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkBringingService(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async removeBringingService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkBringingService(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getBringingServicePrice(cartId: string, transportGroupId: string): Promise<IServicePrice> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getBringingServicePrice(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async addAssemblyService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.checkAssemblyService(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async removeAssemblyService(cartId: string, transportGroupId: string): Promise<{
    success: boolean;
  }> {
    const method = 'DELETE';
    const {
      json
    } = await fetchNext(this.endpoints.checkAssemblyService(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getAssemblyServicePrice(cartId: string, transportGroupId: string): Promise<IServicePrice[]> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getAssemblyServicePrice(cartId, transportGroupId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setSelectedTaxRegimeDataId(selectedTaxRegimeDataId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.selectedTaxRegimeDataId(), {
      method,
      body: JSON.stringify({
        selectedTaxRegimeDataId
      })
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSelectedTaxRegimeDataId() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.selectedTaxRegimeDataId(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getPointsOfDelivery(location: GeolocationCoordinates | null, cartId: string, transportGroupId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getPointsOfDelivery(cartId, location, transportGroupId), {
      method
    });
    return json.data;
  }
  public async getPointsOfSale() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getPointsOfSale(), {
      method
    });
    return json.data;
  }
  public async getBNPHash() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getBNPHash(), {
      method
    }, NextCheckoutApi.getLocale());
    return json.data;
  }
  public async setSessionNote(note: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.sessionNote(), {
      method,
      body: JSON.stringify(note)
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getSessionNote() {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.sessionNote(), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async addService(cartId: string, transportGroupId: string, serviceId: string): Promise<{
    success: boolean;
  }> {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.service(cartId, transportGroupId, serviceId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getCartAssemblyAgreement(cartId: string) {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.cartAgreement(cartId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async setCartAssemblyAgreement(cartId: string) {
    const method = 'POST';
    const {
      json
    } = await fetchNext(this.endpoints.cartAgreement(cartId), {
      method
    }, NextCheckoutApi.getLocale());
    return json;
  }
  public async getCartDeliveries(cartId: string, destination?: IDeliveryDestination): Promise<ICartDeliveriesResponse> {
    const method = 'GET';
    const {
      json
    } = await fetchNext(this.endpoints.getCartDeliveries(cartId, destination), {
      method
    }, NextCheckoutApi.getLocale());

    // fetchNext zwraca { success: true, data: ICartDeliveriesResponse }
    return json.data || json;
  }
  private convertDeliveryItemToCartAvailableDelivery(deliveryItem: IDeliveryItem, postalCode?: string): ICartAvailableDelivery {
    return {
      deliveryType: 'delivery' as DeliveryType,
      // Domyślnie 'delivery', można rozszerzyć logikę
      deliveryTypeId: deliveryItem.deliveryId,
      deliveryTypeNameLanguageKey: deliveryItem.deliveryNameLanguageKey,
      isAssembleAvailable: deliveryItem.options.isAssembleAvailable,
      isBringingAvailable: deliveryItem.options.isBringingAvailable,
      isPaidOnDelivery: deliveryItem.options.isPaidOnDelivery,
      isTaxAvailable: deliveryItem.options.isTaxAvailable,
      isVatAvailable: deliveryItem.options.isTaxAvailable,
      // Mapowanie isTaxAvailable na isVatAvailable
      leadTime: 0,
      // Brak danych w nowym API, można dodać logikę obliczania
      logisticsTime: countBusinessDays(dayjs(), dayjs(deliveryItem.expectedDeliveryDate)),
      // Brak danych w nowym API, można dodać logikę obliczania
      personalPickupStoreType: deliveryItem.personalPickupStoreType,
      price: {
        amount: deliveryItem.pricing.gross.amount,
        currency: deliveryItem.pricing.gross.currency
      },
      postalCode: postalCode || '',
      configuration: {
        additionalFields: deliveryItem.configuration.additionalFields,
        requirements: deliveryItem.configuration.requirements || null
      },
      pricingWithRequiredServices: deliveryItem.pricingWithRequiredServices,
      expectedDeliveryDate: deliveryItem.expectedDeliveryDate
    };
  }
  private convertAdditionalArgumentsToDestination(additionalArguments?: IAdditionalArgument | IAdditionalArguments | IAdditionalArgumentsRecord): IDeliveryDestination | null {
    if (!additionalArguments) return null;
    let postalCode = '';
    let countryCode = '';

    // Obsługa różnych typów argumentów
    if ('key' in additionalArguments && 'value' in additionalArguments) {
      // Pojedynczy IAdditionalArgument
      if (additionalArguments.key === 'postalCode') {
        postalCode = additionalArguments.value;
        countryCode = 'PL'; // Domyślnie PL
      } else if (additionalArguments.key === 'countryCode') {
        countryCode = additionalArguments.value;
      }
    } else if ('arguments' in additionalArguments && Array.isArray(additionalArguments.arguments)) {
      // IAdditionalArguments (tablica)
      const postalCodeArg = additionalArguments.arguments.find(arg => arg.key === 'postalCode');
      const countryCodeArg = additionalArguments.arguments.find(arg => arg.key === 'countryCode');
      postalCode = postalCodeArg?.value || '';
      countryCode = countryCodeArg?.value || 'PL';
    } else if (typeof additionalArguments === 'object' && !('key' in additionalArguments) && !('arguments' in additionalArguments)) {
      // IAdditionalArgumentsRecord (Record<string, string>)
      const recordArgs = additionalArguments;
      postalCode = recordArgs.postalCode || '';
      countryCode = recordArgs.countryCode || 'PL';
    }
    if (postalCode || countryCode) {
      return {
        postalCode,
        countryCode
      };
    }
    return null;
  }
}
export default new NextCheckoutApi();